import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Filter, X, UserPlus, Loader2, Check, Clock, UserCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { PlayerPreviewModal } from "./PlayerPreviewModal";

interface SearchResult {
  id: string;
  name: string;
  email: string | null;
  profileImageUrl: string | null;
  skillLevel: number | null;
  isFriend: boolean;
  friendshipStatus?: string | null;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map(n => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";
}

export function SearchPlayers() {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [levelFilter, setLevelFilter] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null);
  const [playerModalOpen, setPlayerModalOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: results = [], isLoading, isFetching } = useQuery<SearchResult[]>({
    queryKey: ['/api/players/search', debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery || debouncedQuery.length < 2) return [];
      const res = await fetch(`/api/players/search?q=${encodeURIComponent(debouncedQuery)}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Search failed');
      return res.json();
    },
    enabled: debouncedQuery.length >= 2,
  });

  const [pendingRequests, setPendingRequests] = useState<Set<string>>(new Set());

  const sendRequestMutation = useMutation({
    mutationFn: async (addresseeId: string) => {
      setPendingRequests(prev => new Set(prev).add(addresseeId));
      await apiRequest("POST", "/api/friends/request", { addresseeId });
      return addresseeId;
    },
    onSuccess: (addresseeId) => {
      queryClient.invalidateQueries({ queryKey: ['/api/players/search', debouncedQuery] });
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
      toast({
        title: "¡Solicitud enviada!",
        description: "Se ha enviado la solicitud de amistad.",
      });
    },
    onError: (error: Error, addresseeId) => {
      toast({
        title: "Error",
        description: "No se pudo enviar la solicitud.",
        variant: "destructive",
      });
    },
    onSettled: (_, __, addresseeId) => {
      setPendingRequests(prev => {
        const next = new Set(prev);
        next.delete(addresseeId);
        return next;
      });
    },
  });

  const filteredResults = results.filter(player => {
    if (levelFilter === "all") return true;
    const level = player.skillLevel || 0;
    if (levelFilter === "beginner") return level <= 2;
    if (levelFilter === "intermediate") return level >= 3 && level <= 4;
    if (levelFilter === "advanced") return level >= 5;
    return true;
  });

  const activeFilters = levelFilter !== "all" ? 1 : 0;

  const renderActionButton = (player: SearchResult) => {
    if (player.isFriend) {
      return (
        <Badge variant="secondary" className="bg-green-500/20 text-green-700 dark:text-green-400 border-0">
          <Check className="h-3 w-3 mr-1" />
          Amigo
        </Badge>
      );
    }

    // User sent a request to this player - show "Pendiente"
    if (player.friendshipStatus === "pending" || pendingRequests.has(player.id)) {
      return (
        <Badge variant="secondary" className="bg-amber-500/20 text-amber-700 dark:text-amber-400 border-0">
          <Clock className="h-3 w-3 mr-1" />
          Pendiente
        </Badge>
      );
    }

    // This player sent a request to the user - show "Responder"
    if (player.friendshipStatus === "pending_received") {
      return (
        <Badge variant="secondary" className="bg-blue-500/20 text-blue-700 dark:text-blue-400 border-0">
          <UserCheck className="h-3 w-3 mr-1" />
          Responder en Social
        </Badge>
      );
    }

    const isLoading = sendRequestMutation.isPending && sendRequestMutation.variables === player.id;

    return (
      <Button
        variant="outline"
        size="sm"
        className="h-8"
        onClick={() => sendRequestMutation.mutate(player.id)}
        disabled={isLoading}
        data-testid={`button-add-friend-${player.id}`}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            <UserPlus className="h-4 w-4 mr-1" />
            Agregar
          </>
        )}
      </Button>
    );
  };

  return (
    <div className="space-y-4" data-testid="section-search-players">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar jugadores por nombre..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="input-search-players"
          />
          {isFetching && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
        <Button
          variant={showFilters ? "secondary" : "outline"}
          onClick={() => setShowFilters(!showFilters)}
          data-testid="button-toggle-filters"
        >
          <Filter className="h-4 w-4" />
          {activeFilters > 0 && (
            <Badge variant="default" className="ml-1 h-5 w-5 p-0 text-xs">
              {activeFilters}
            </Badge>
          )}
        </Button>
      </div>

      {showFilters && (
        <div className="flex flex-wrap gap-3 p-3 bg-muted/50 rounded-lg">
          <div className="flex-1 min-w-32">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Nivel
            </label>
            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger data-testid="select-level-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los Niveles</SelectItem>
                <SelectItem value="beginner">Principiante (0-2)</SelectItem>
                <SelectItem value="intermediate">Intermedio (3-4)</SelectItem>
                <SelectItem value="advanced">Avanzado (5+)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {activeFilters > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="self-end"
              onClick={() => setLevelFilter("all")}
              data-testid="button-clear-filters"
            >
              <X className="h-4 w-4 mr-1" />
              Limpiar
            </Button>
          )}
        </div>
      )}

      <div className="space-y-2">
        {searchQuery.length < 2 ? (
          <Card>
            <CardContent className="py-8">
              <div className="text-center text-muted-foreground">
                <Search className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Escribe al menos 2 caracteres para buscar</p>
              </div>
            </CardContent>
          </Card>
        ) : isLoading ? (
          <Card>
            <CardContent className="py-8">
              <div className="flex justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        ) : filteredResults.length > 0 ? (
          filteredResults.map((player) => (
            <Card 
              key={player.id} 
              className={`cursor-pointer hover-elevate ${player.isFriend ? "border-green-500/30" : ""}`}
              onClick={() => {
                setSelectedPlayerId(player.id);
                setPlayerModalOpen(true);
              }}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <Avatar className={`h-12 w-12 border-2 ${player.isFriend ? "border-green-500/30" : "border-primary/30"}`}>
                    <AvatarImage src={player.profileImageUrl || undefined} alt={player.name} />
                    <AvatarFallback className={`font-semibold ${player.isFriend ? "bg-green-500/20 text-green-700 dark:text-green-400" : "bg-primary/10 text-primary"}`}>
                      {getInitials(player.name)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate" data-testid={`text-player-name-${player.id}`}>
                        {player.name}
                      </span>
                    </div>
                    {player.skillLevel !== null && (
                      <div className="text-xs text-muted-foreground">
                        Nivel {player.skillLevel.toFixed(1)} • Pádel
                      </div>
                    )}
                  </div>

                  <div onClick={(e) => e.stopPropagation()}>
                    {renderActionButton(player)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="py-8">
              <div className="text-center text-muted-foreground">
                <p>No se encontraron jugadores con "{searchQuery}"</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <PlayerPreviewModal
        playerId={selectedPlayerId}
        open={playerModalOpen}
        onOpenChange={setPlayerModalOpen}
      />
    </div>
  );
}
